import { BranchCode } from "api/risk";
import { PersonDto } from "api/contract";
import { SearchPersonResult } from "components/domain/SearchBar/types";
import { ActorForm } from "features/new-actor/components/ActorForm";
import { ActorLayoutVariant } from "features/new-actor/enums/actorLayoutVariant";
import { NaturalPersonFormValues } from "features/new-actor/types/naturalPersonFormValues";
import { useSubscriptionProcessActions } from "features/subscription-process/contexts/SubscriptionProcessContext";
import { useActorRegistrationPanel } from "features/subscription-process/registration-process/hooks/useActorRegistrationPanel";
import { usePanel } from "hooks/usePanel";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { FC, useCallback, useMemo, useState } from "react";
import { ExistingCompanySearch } from "./ExistingCompanySearch";
import { LegalProtectionPanelDivider } from "./LegalProtectionPanelDivider";

// Accounting data are entered with the company; its subsidiaries have their own section on the page.
// The form sits in the side panel: its lists take the compact layout.
const COMPANY_FORM_OPTIONS = { financials: true, compact: true };

interface UpsertLegalProtectionCompanyProps {
  /** Company of the folder, to edit. Omitted: creation, and the search is offered. */
  company?: PersonDto;
  /** Folder to register the company on. The panel is outside the route: it cannot read it from the URL. */
  riskId: string;
  /** Reloads the step data (accounting data, verdict) once the company is saved. Given by the page: a panel reads nothing itself. */
  onSaved?: () => void | Promise<void>;
}

/**
 * Company of a legal protection folder, created or edited in the side panel.
 * The form carries the company and its accounting data; its subsidiaries are saved by their
 * own section of the step page.
 */
export const UpsertLegalProtectionCompany: FC<UpsertLegalProtectionCompanyProps> = ({ company, riskId, onSaved }) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const { closePanel } = usePanel();
  const isEdit = Boolean(company);
  // Company picked in the search: the form switches to editing it.
  const [pickedCompany, setPickedCompany] = useState<PersonDto | undefined>(undefined);

  const editedCompany = company ?? pickedCompany;

  // Saved company: reload the folder (the step page reads it back), then close the panel.
  const handleSaved = useCallback(async () => {
    await refreshSubscriptionProcess();
    await onSaved?.();
    closePanel();
  }, [refreshSubscriptionProcess, onSaved, closePanel]);

  const handleSelectCompany = useCallback((person: SearchPersonResult) => {
    setPickedCompany(person.data as PersonDto);
  }, []);

  const {
    formConfig,
    editConfig,
    formikRef,
    handleSubmit,
    handleValidationChange,
    selectedPerson,
  } = useActorRegistrationPanel(BranchCode.LegalProtection, {
    initialPerson: editedCompany,
    postSubmit: handleSaved,
    // The risk is only given to register a company on the folder. The company of the folder is
    // already registered: saving it with the risk would ask the back to register it a second
    // time, which it refuses. It is then saved as a person alone, like in the other branches.
    riskId: isEdit ? undefined : riskId,
    // The form carries no subsidiaries: the accounting data are saved alone, never through the subsidiaries sync.
    withFinancials: true,
  });

  // The picked company changes the key: the form remounts with its values.
  const formKey = useMemo(() => selectedPerson?.id ?? 'new', [selectedPerson]);

  return (
    <div className="flex flex-col gap-7">
      {!isEdit && (
        <>
          <ExistingCompanySearch onSelect={handleSelectCompany} isSelected={Boolean(pickedCompany)} />
          <LegalProtectionPanelDivider
            label={pickedCompany
              ? t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.PANEL.COMPANY_SELECTED")
              : t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.PANEL.OR_ENTER_COMPANY")}
          />
        </>
      )}
      <ActorForm<NaturalPersonFormValues>
        key={formKey}
        layoutVariant={ActorLayoutVariant.LegalProtectionActor}
        legalEntityOptions={COMPANY_FORM_OPTIONS}
        formConfig={formConfig}
        formikRef={formikRef}
        editConfig={editConfig}
        onValidationChange={handleValidationChange}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
